import { useIsFormDisabled } from "@/flokit/compound/inputs/contexts/disabled-form-context";
import { cx } from "class-variance-authority";
import { omit } from "lodash";
import { FormEvent, HTMLProps } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import InputIcon from "react-multi-date-picker/components/input_icon";
import { FormLabel } from "../form-label/form-label";
import InputLabel from "../input-general/input-label";
import { normalizeLabelProp } from "../input-general/label-prop";
import ValidationMessage from "../input-general/validation";
import { useHtmlId } from "../utils/useHtmlId";

interface SelectDateTimeProps extends HTMLProps<HTMLInputElement> {
  type: "date" | "time" | "datetime-local";
  tooltip?: string;
  label?: string;
  inputId?: string;
  onChange?: (e: FormEvent<HTMLInputElement>) => void;
  isValid?: boolean;
  validationMessage?: string;
  outlineColor?: "navy" | "magenta"; // magenta is the default when not specified
}

export const SelectDateTime = (props: SelectDateTimeProps) => {
  const isDisabled = useIsFormDisabled();
  return (
    <div className="flex w-full flex-col gap-y-2">
      {props.label ? (
        <FormLabel
          htmlFor={props.inputId}
          name={props.name}
          label={props.label}
          required={props.required}
          tooltip={props.tooltip}
        />
      ) : null}

      <input
        id={props.inputId}
        disabled={isDisabled}
        className={cx(
          "h-[39px] px-4",
          "rounded border border-dublin-gray",
          "text-base",
          props.outlineColor === "navy"
            ? "focus:border-navy focus:ring-navy"
            : "focus:border-flo-magenta focus:ring-flo-magenta",
          "focus:outline-none focus:ring-1",
          "disabled:text-light-elephant",
        )}
        {...omit(props, "isValid")}
        type={props.type}
        name={props.name}
        onChange={(e) => {
          props.onChange(e as FormEvent<HTMLInputElement>);
        }}
        value={props.value || ""}
      />
      <ValidationMessage
        shouldShowValidation={!props.isValid}
        validationMessage={props.validationMessage}
      />
    </div>
  );
};

interface SelectMultipleDatesCommonProps {
  name?: string;
  label?: string;
  required?: boolean;
  tooltip?: string;
  UNSAFE_className?: string;
}

// Seperate props depending on if single or multi date select
type SelectMultipleDatesProps =
  | (SelectMultipleDatesCommonProps & {
      pickSingleDate?: false;
      onChange: (dates: DateObject[]) => void;
      value: Date[] | DateObject[];
    })
  | (SelectMultipleDatesCommonProps & {
      pickSingleDate: true;
      onChange: (date: DateObject) => void;
      value: Date | DateObject;
    });

export const SelectMultipleDates = (props: SelectMultipleDatesProps) => {
  const generatedId = useHtmlId();
  const labelMethod = normalizeLabelProp(props.label);

  return (
    <div className="flex w-full flex-col gap-y-2">
      {props.label ? (
        <InputLabel
          id={generatedId}
          labelMethod={labelMethod}
          name={props.name}
          required={props.required}
          tooltip={props.tooltip}
          isElementInteractive={true}
        />
      ) : null}
      <div aria-label={props.label} className="flex w-full flex-col">
        <DatePicker
          render={
            <InputIcon
              aria-label={"Date Input"}
              className={props.UNSAFE_className}
              placeholder="Select Dates"
            />
          }
          multiple={!props.pickSingleDate}
          sort
          value={props.value}
          onChange={props.onChange}
          format={"MM/DD/YYYY"}
        />
      </div>
    </div>
  );
};
